import { platform } from "../platform/index.js";
// Raw Yahoo chart-endpoint access — moved verbatim from market-data.ts
// when the provider layer landed (D-022). This is a free-aggregate SOURCE,
// not a provider: free-provider.ts composes it with others.
//
// Not a documented/supported API — Yahoo can change or restrict it without
// notice. Each running copy of the extension only calls this for its own
// small watchlist on a ~30 minute cadence, staggered per symbol, to stay
// well under anything that would look automated/abusive.

import { MarketDataError, type OHLCPoint } from "../market-data.js";

interface YahooChartResponse {
  chart: {
    result?: Array<{
      meta: { regularMarketPrice?: number };
      timestamp?: number[];
      indicators: {
        quote: Array<{
          open?: (number | null)[];
          high?: (number | null)[];
          low?: (number | null)[];
          close: (number | null)[];
          volume?: (number | null)[];
        }>;
        adjclose?: Array<{ adjclose?: (number | null)[] }>;
      };
    }>;
  };
}

async function fetchYahooChartFrom(host: string, symbol: string, range: string, interval: string): Promise<YahooChartResponse> {
  const url = `https://${host}/v8/finance/chart/${encodeURIComponent(
    symbol
  )}?range=${range}&interval=${interval}&includePrePost=false`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await platform.dataFetch(url, { signal: controller.signal });
    if (!response.ok) throw new MarketDataError(`Yahoo chart request failed: ${response.status}`);
    return (await response.json()) as YahooChartResponse;
  } finally {
    clearTimeout(timeout);
  }
}

/** query1 with query2 failover — same data, independent frontends, so a
 * rate-limit or outage on one host doesn't blank the product. */
async function fetchYahooChart(symbol: string, range: string, interval: string): Promise<YahooChartResponse> {
  try {
    return await fetchYahooChartFrom("query1.finance.yahoo.com", symbol, range, interval);
  } catch {
    return await fetchYahooChartFrom("query2.finance.yahoo.com", symbol, range, interval);
  }
}

export async function yahooCandles(symbol: string, range: string, interval: string): Promise<{ candles: OHLCPoint[]; currentPrice?: number }> {
  const decoded = await fetchYahooChart(symbol, range, interval);
  const result = decoded.chart.result?.[0];
  if (!result) throw new MarketDataError("No data in Yahoo response");

  const quote = result.indicators.quote[0];
  const opens = quote?.open ?? [];
  const highs = quote?.high ?? [];
  const lows = quote?.low ?? [];
  const closes = quote?.close ?? [];
  const volumes = quote?.volume ?? [];
  // Split/dividend-adjusted closes (present for equities/ETFs; equals the
  // raw close for futures/forex/crypto). Without this, a stock split shows
  // as an artificial price cliff and every SMA across it is garbage. We
  // scale the whole candle by adjclose/close so wicks stay consistent; the
  // factor is ~1.0 at the latest bar, so there's no seam with live price.
  const adjcloses = result.indicators.adjclose?.[0]?.adjclose ?? [];
  const timestamps = result.timestamp ?? [];

  const candles: OHLCPoint[] = [];
  for (let i = 0; i < timestamps.length; i++) {
    const open = opens[i];
    const high = highs[i];
    const low = lows[i];
    const close = closes[i];
    if (open == null || high == null || low == null || close == null) continue;
    const adj = adjcloses[i];
    const factor = adj != null && close > 0 ? adj / close : 1;
    const volume = volumes[i] ?? 0;
    candles.push({
      date: new Date(timestamps[i]! * 1000),
      open: open * factor,
      high: high * factor,
      low: low * factor,
      close: adj != null ? adj : close,
      volume: volume ?? 0,
    });
  }
  return { candles, currentPrice: result.meta.regularMarketPrice };
}

export async function yahooCloses(
  symbol: string,
  range: string,
  interval: string
): Promise<{ closes: number[]; regularMarketPrice?: number }> {
  const decoded = await fetchYahooChart(symbol, range, interval);
  const result = decoded.chart.result?.[0];
  if (!result) throw new MarketDataError("No data in Yahoo response");
  const closes = (result.indicators.quote[0]?.close ?? []).filter((c): c is number => c != null);
  return { closes, regularMarketPrice: result.meta.regularMarketPrice };
}
